const React = require('react');
const useForm = require('hooks/useForm');
const UseFormStateUtils = require('./state');

const UseFormHookUtils = module.exports = {};

const {
    useEffect,
    useMemo,
    useRef,
    useState
} = React;

UseFormHookUtils.useLocationStateActions = ({
    locationState,
    buildLocationStateActions
}) => useMemo(
    () => buildLocationStateActions({
        locationState
    }),
    [buildLocationStateActions, locationState]
);

UseFormHookUtils.useManagedDefaults = ({
    props,
    buildDefaults,
    deps = []
}) => useMemo(
    () => buildDefaults(props),
    deps
);

UseFormHookUtils.useInitialState = ({
    props,
    buildInitialState,
    deps = []
}) => useMemo(
    () => buildInitialState(props),
    deps
);

UseFormHookUtils.useSchema = ({
    schema,
    buildSchema
}) => useMemo(
    () => schema || (typeof buildSchema === 'function' ? buildSchema() : undefined),
    [buildSchema, schema]
);

UseFormHookUtils.useResetFormDefaults = ({
    initialState,
    defaults,
    methods,
    shouldSyncStateDefaults
}) => {

    useEffect(() => {

        if (!shouldSyncStateDefaults({
            nextInitialState: initialState,
            isFormDirty: !!methods.formState?.isDirty
        })) {
            return;
        }

        methods.reset?.(defaults);
    }, [defaults, initialState, methods, shouldSyncStateDefaults]);
};

UseFormHookUtils.useFormInstanceKey = ({
    initialState,
    isFormDirty,
    syncProps,
    shouldSyncStateDefaults,
    shouldSyncStateDefaultsAfterPropsChange = UseFormStateUtils.shouldSyncStateDefaultsAfterPropsChange
}) => {

    const [instanceKey, setInstanceKey] = useState(0);
    const prevSyncPropsRef = useRef(syncProps);

    useEffect(() => {

        const prevProps = prevSyncPropsRef.current;

        if (
            shouldSyncStateDefaultsAfterPropsChange({
                props: syncProps,
                prevProps
            })
            && shouldSyncStateDefaults({
                nextInitialState: initialState,
                isFormDirty
            })
        ) {
            setInstanceKey((key) => key + 1);
        }

        prevSyncPropsRef.current = syncProps;
    }, [
        initialState,
        isFormDirty,
        shouldSyncStateDefaults,
        shouldSyncStateDefaultsAfterPropsChange,
        syncProps
    ]);

    return instanceKey;
};

UseFormHookUtils.useStillMountedRef = () => {

    const stillMountedRef = useRef(true);

    useEffect(() => () => {

        stillMountedRef.current = false;
    }, []);

    return stillMountedRef;
};

UseFormHookUtils.useManagedFormState = ({
    props,
    formConfig,
    fields = formConfig?.formFields,
    schema,
    buildSchema,
    buildDefaults = (_props) => formConfig?.buildManagedFormDefaults?.({
        props: _props
    }),
    defaultsDeps,
    buildInitialState = (_props) => formConfig?.buildInitialManagedState?.(_props),
    initialStateDeps,
    buildLocationStateActions,
    shouldSyncStateDefaults = formConfig?.shouldSyncStateDefaults || UseFormStateUtils.shouldSyncStateDefaults,
    shouldSyncStateDefaultsAfterPropsChange = formConfig?.shouldSyncStateDefaultsAfterPropsChange,
    syncDefaults = false,
    debug = false,
    title,
    formId
}) => {

    const locationStateActions = UseFormHookUtils.useLocationStateActions({
        locationState: props.location?.state,
        buildLocationStateActions
    });
    const managedDefaults = UseFormHookUtils.useManagedDefaults({
        props,
        buildDefaults,
        deps: defaultsDeps
    });
    const initialState = UseFormHookUtils.useInitialState({
        props,
        buildInitialState,
        deps: initialStateDeps
    });
    const resolvedSchema = UseFormHookUtils.useSchema({
        schema,
        buildSchema
    });

    const {
        methods,
        fields: normalizedFields,
        defaults
    } = useForm({
        defaults: managedDefaults,
        onSubmit: () => null,
        fields,
        schema: resolvedSchema,
        syncDefaults,
        debug,
        title,
        formId
    });

    UseFormHookUtils.useResetFormDefaults({
        initialState,
        defaults: managedDefaults,
        methods,
        shouldSyncStateDefaults
    });

    const instanceKey = UseFormHookUtils.useFormInstanceKey({
        initialState,
        isFormDirty: !!methods.formState?.isDirty,
        syncProps: props,
        shouldSyncStateDefaults,
        shouldSyncStateDefaultsAfterPropsChange
    });
    const stillMountedRef = UseFormHookUtils.useStillMountedRef();

    return {
        defaults,
        fields: normalizedFields,
        initialState,
        instanceKey,
        locationStateActions,
        methods,
        stillMountedRef
    };
};
